import { Autocomplete, TextField } from '@mui/material';
import type { ErpIntermediateMetadata } from '@vy/types';
import { isEmptyArray, isEmptyString, isNullOrUndefined } from '@vy/utils';
import { useMemo } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useXeroProducts } from '../../../../../../../../hooks/requests/integrations/xero/use-xero-products';
import type { FormattedIntegrationProduct } from '../../../../../../../../utils/types/formatted-integration-product';

type ControlledXeroProductDropdownProps<FormType extends FieldValues> = {
  fieldPath: FieldPath<FormType>;
  control: Control<FormType>;
  showDropdown: boolean;
  placeholder?: string;
  onChange?: (value: ErpIntermediateMetadata | null) => void;
};

export function ControlledXeroProductDropdown<FormType extends FieldValues>({
  fieldPath,
  control,
  showDropdown,
  placeholder = 'Select Xero Product',
  onChange,
}: ControlledXeroProductDropdownProps<FormType>) {
  const { data } = useXeroProducts();

  const formattedProducts = useMemo((): FormattedIntegrationProduct[] => {
    const products = data?.products;

    if (isEmptyArray(products)) {
      return [];
    }

    return products.map(({ id, name, code }) => ({
      id,
      name,
      code,
    }));
  }, [data]);

  return (
    <Controller
      control={control}
      name={fieldPath}
      render={({ field }) => {
        const currentValue = field.value as ErpIntermediateMetadata | null;
        const matchingProduct = findMatchingProduct(formattedProducts, currentValue);

        const handleProductChange = (_: unknown, selectedProduct: FormattedIntegrationProduct | null) => {
          const erpMetadata = selectedProduct ? createErpMetadata(selectedProduct) : null;
          field.onChange(erpMetadata);
          onChange?.(erpMetadata);
        };

        const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          if (isEmptyString(event.target.value)) {
            field.onChange(null);
          }
        };

        return (
          <Autocomplete<FormattedIntegrationProduct>
            disabled={!showDropdown}
            options={formattedProducts}
            value={matchingProduct}
            getOptionLabel={(option) => option.name}
            getOptionDisabled={(option) => option.id === currentValue?.erpId}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={handleProductChange}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                onChange={handleInputChange}
              />
            )}
          />
        );
      }}
    />
  );
}

function createErpMetadata(product: FormattedIntegrationProduct): ErpIntermediateMetadata {
  return {
    erpId: product.id,
    erpName: product.name,
    code: product.code,
  };
}

function findMatchingProduct(
  products: FormattedIntegrationProduct[],
  currentValue: ErpIntermediateMetadata | null,
): FormattedIntegrationProduct | null {
  if (isNullOrUndefined(currentValue) || isEmptyArray(products)) {
    return null;
  }

  return products.find((product) => product.id === currentValue.erpId) ?? null;
}
